import React from 'react'

import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'components/ui/sheet'
import { adminEmployers, adminQuote } from 'rails_routes'
import { Badge } from 'slab'
import { getEnglishLanguageDateFromDateString, getLocaleCurrencyStringFromString } from 'utils'
import { useGetAdminEmployerOpenEnrollmentId } from 'utils/web_api'

interface EmployerOpenEnrollmentDetailsSheetProps {
  configId: string | null
  onClose: () => void
}

const STATUS_BADGE_TYPES: Record<string, 'neutral' | 'info' | 'caution' | 'warning' | 'success'> = {
  not_ready: 'neutral',
  ready_for_open_enrollment: 'info',
  employees_confirmed: 'caution',
  accepted_preliminary_quote: 'warning',
  accepted_final_quote: 'success',
}

const allowanceTypeToText: Record<string, string> = {
  flat: 'Flat',
  dynamic: 'Dynamic',
  not_set: 'Not Set',
}

const EmployerOpenEnrollmentDetailsSheet: React.FC<EmployerOpenEnrollmentDetailsSheetProps> = ({
  configId,
  onClose,
}) => {
  if (!configId) return null

  return <EmployerOpenEnrollmentDetailsSheetContent configId={configId} onClose={onClose} />
}

const EmployerOpenEnrollmentDetailsSheetContent: React.FC<{
  configId: string
  onClose: () => void
}> = ({ configId, onClose }) => {
  const { data, isLoading } = useGetAdminEmployerOpenEnrollmentId(configId)

  if (isLoading) return <div className='p-6'>Loading...</div>
  if (!data) return <div className='p-6'>Failed to load details</div>

  return (
    <Sheet open onOpenChange={() => onClose()}>
      <SheetContent className='h-dvh overflow-y-auto' style={{ minWidth: '600px' }} aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle>Open Enrollment Details</SheetTitle>
        </SheetHeader>

        <div className='space-y-6 p-6'>
          {/* Employer Information */}
          <div className='space-y-4' data-testid='employer-info'>
            <h3 className='text-lg font-semibold'>Employer</h3>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='text-sm font-medium text-gray-500'>Name</label>
                <div data-testid='employer-name'>{data.profile?.name || 'Unknown'}</div>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>DBA</label>
                <div data-testid='employer-dba'>{data.profile?.dba || '—'}</div>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>Admin Link</label>
                <div>
                  <a
                    href={adminEmployers.show({ id: data.id })}
                    className='text-blue-600 hover:text-blue-800 underline'
                    target='_blank'
                    rel='noopener noreferrer'
                    data-testid='employer-admin-link'
                  >
                    View in Admin
                  </a>
                </div>
              </div>
              <div>
                <label className='text-sm font-medium text-gray-500'>Billing Status</label>
                <div className='capitalize' data-testid='employer-billing-status'>{data.billing_status?.replace('_', ' ')}</div>
              </div>
            </div>
          </div>

          {/* Open Enrollment Config */}
          {data.open_enrollment_config && (
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Open Enrollment Configuration</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Cohort</label>
                  <div className='capitalize'>
                    {data.open_enrollment_config.cohort?.replace(/_/g, ' ')}
                  </div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Status</label>
                  <div>
                    {data.open_enrollment_config.status && (
                      <Badge
                        type={STATUS_BADGE_TYPES[data.open_enrollment_config.status] || 'neutral'}
                      >
                        {data.open_enrollment_config.status.split('_').map((word: string) =>
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </Badge>
                    )}
                  </div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Eligible Employees</label>
                  <div>{data.open_enrollment_config.eligible_employee_count}</div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>% Employees with Final Rates</label>
                  <div>{data.open_enrollment_config.percentage_of_employees_with_final_rates}%</div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Current Monthly Contribution</label>
                  <div>{data.open_enrollment_config.current_year_monthly_employer_contribution?.formatted_amount || '—'}</div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>New Monthly Contribution</label>
                  <div>{data.open_enrollment_config.open_enrollment_year_monthly_employer_contribution?.formatted_amount || '—'}</div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Contribution Delta</label>
                  <div>
                    {(() => {
                      const current = data.open_enrollment_config.current_year_monthly_employer_contribution?.subunits
                      const newAmount = data.open_enrollment_config.open_enrollment_year_monthly_employer_contribution?.subunits
                      if (current === undefined || newAmount === undefined) return '—'
                      const delta = newAmount - current
                      return getLocaleCurrencyStringFromString((delta / 100.0).toFixed(2))
                    })()}
                  </div>
                </div>
              </div>
              <div className='space-y-2'>
                <label className='text-sm font-medium text-gray-500'>States with Final Rates</label>
                <div className='flex flex-wrap gap-2'>
                  {data.open_enrollment_config.states_with_final_rates?.length
                    ? (
                        data.open_enrollment_config.states_with_final_rates.map(state => (
                          <span key={state} className='px-2 py-1 bg-green-100 text-green-800 rounded text-sm'>
                            {state}
                          </span>
                        ))
                      )
                    : (
                      <span className='text-gray-400'>None</span>
                      )}
                </div>
              </div>
              <div className='space-y-2'>
                <label className='text-sm font-medium text-gray-500'>States with Preliminary Rates</label>
                <div className='flex flex-wrap gap-2'>
                  {data.open_enrollment_config.states_with_preliminary_rates?.length
                    ? (
                        data.open_enrollment_config.states_with_preliminary_rates.map(state => (
                          <span key={state} className='px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm'>
                            {state}
                          </span>
                        ))
                      )
                    : (
                      <span className='text-gray-400'>None</span>
                      )}
                </div>
              </div>
            </div>
          )}

          {/* Renewal Config */}
          {data.open_enrollment_config?.employer_renewal_config && (
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Renewal Configuration</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Current Year Quote</label>
                  <div>
                    {data.open_enrollment_config.employer_renewal_config.current_year_quote?.id &&
                       data.open_enrollment_config.employer_renewal_config.prospect_id
                      ? (
                        <a
                          href={adminQuote.explore({ prospect_id: data.open_enrollment_config.employer_renewal_config.prospect_id, id: data.open_enrollment_config.employer_renewal_config.current_year_quote?.id })}
                          className='text-blue-600 hover:text-blue-800 underline'
                          target='_blank'
                          rel='noopener noreferrer'
                          data-testid='current-year-quote-link'
                        >
                          View Quote
                        </a>
                        )
                      : '—'}
                  </div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Renewal Year Quote</label>
                  <div>
                    {data.open_enrollment_config.employer_renewal_config.renewal_year_quote?.id &&
                       data.open_enrollment_config.employer_renewal_config.prospect_id
                      ? (
                        <a
                          href={adminQuote.explore({ prospect_id: data.open_enrollment_config.employer_renewal_config.prospect_id, id: data.open_enrollment_config.employer_renewal_config.renewal_year_quote?.id })}
                          className='text-blue-600 hover:text-blue-800 underline'
                          target='_blank'
                          rel='noopener noreferrer'
                          data-testid='renewal-year-quote-link'
                        >
                          View Quote
                        </a>
                        )
                      : '—'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ICHRA Config */}
          {data.ichra_config && (
            <div className='space-y-4' data-testid='ichra-config'>
              <h3 className='text-lg font-semibold'>ICHRA Configuration</h3>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Coverage Start Date</label>
                  <div>
                    {data.ichra_config.coverage_start_date
                      ? getEnglishLanguageDateFromDateString(data.ichra_config.coverage_start_date)
                      : '—'}
                  </div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Include Dental</label>
                  <div>{data.ichra_config.include_dental ? 'Yes' : 'No'}</div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Include Vision</label>
                  <div>{data.ichra_config.include_vision ? 'Yes' : 'No'}</div>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-500'>Allowance Type</label>
                  <div className='capitalize'>{allowanceTypeToText[data.ichra_config.allowance_type || 'not_set'] || '—'}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default EmployerOpenEnrollmentDetailsSheet
